'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createClient } from '@/lib/supabase/client'
import { humanizeAuthError } from '@/lib/auth-errors'
import { resetPasswordRequestSchema } from '@/lib/validations/schemas'

/**
 * Task #142 — real passwordless / magic-link request.
 *
 * Before this commit /login/magic was a dead `redirect('/login')` stub even
 * though /login/password advertised "Send me an email link". This calls
 * supabase.auth.signInWithOtp with emailRedirectTo pointing at the existing
 * /api/auth/callback handler (the same handler OAuth + email-confirm use),
 * then routes the user to the "check your email" screen.
 */
export function MagicLinkForm() {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    const formData = new FormData(e.currentTarget)
    const parsed = resetPasswordRequestSchema.safeParse({
      email: String(formData.get('email') ?? ''),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a valid email address')
      return
    }

    setIsPending(true)
    try {
      const supabase = createClient()
      const callbackUrl = `${window.location.origin}/api/auth/callback?redirect=auto`
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: parsed.data.email,
        options: { emailRedirectTo: callbackUrl },
      })

      if (otpError) {
        setError(humanizeAuthError(otpError.message))
        setIsPending(false)
        return
      }

      router.push(
        `/login/magic/sent?email=${encodeURIComponent(parsed.data.email)}`,
      )
    } catch {
      setError('Network error. Please check your connection and try again.')
      setIsPending(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          enterKeyHint="go"
          placeholder="you@example.com"
          className="h-11 text-base"
          aria-invalid={!!error}
          aria-describedby={error ? 'magic-error' : undefined}
          required
        />
      </div>

      {error && (
        <p id="magic-error" role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" className="w-full h-11 text-base" disabled={isPending}>
        {isPending ? 'Sending link…' : 'Email me a sign-in link'}
      </Button>
    </form>
  )
}
